import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import api from '../../../../middleware/api';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';

const ResetPassword = () => {
    const [searchParam] = useSearchParams();
    const token = searchParam.get('token');
    const navigate = useNavigate();
    const [resetSuccess, setResetSuccess] = useState(false);

    // Validation schema using Yup
    const validationSchema = Yup.object({
        password: Yup.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères').required('Requis'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Les mots de passe doivent correspondre')
            .required('Requis'),
    });

    const handleGoToLogin = () => {
        navigate('/se-connecter');
    }

    const handleResetPassword = async (values, { setSubmitting }) => {
        try {
            // Envoi de la demande de réinitialisation du mot de passe avec le nouveau mot de passe
            const response = await api.post('/auth/reset-password', {password: values.password, token});
            if (response.status === 200) {
                setResetSuccess(true);
                toast.success('Votre mot de passe a été réinitialisé avec succès.');
            } else {
                toast.error('Échec de la réinitialisation du mot de passe. Veuillez réessayer.');
            }
        } catch (error) {
            toast.error('Une erreur est survenue. Veuillez réessayer plus tard.');
        } finally {
            setSubmitting(false);
        }
    };

    if (resetSuccess) {
        return (
            <div className="flex flex-col justify-center items-center w-3/4 max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg relative">
                <div className="flex flex-col items-center pt-4">
                    <div className="bg-green-100 p-3 rounded-full mb-6">
                        <CheckCircleIcon className="text-green-500 h-12 w-12" />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Mot de passe réinitialisé !</h2>
                    
                    <div className="text-center mb-6">
                        <p className="text-gray-600 mb-4">
                            Votre mot de passe a été réinitialisé avec succès.
                        </p>
                        <p className="text-gray-600">
                            Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
                        </p>
                    </div>
                    
                    <button
                        onClick={handleGoToLogin}
                        className="mt-4 bg-primary hover:bg-primary-dark text-white font-medium py-3 px-6 rounded-md transition duration-300 shadow-sm"
                    >
                        Se connecter
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col justify-center items-center w-3/4 max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg relative">
            <button 
                onClick={handleGoToLogin} 
                className="absolute top-4 left-4 flex items-center text-gray-600 hover:text-primary transition-colors"
            >
                <ArrowLeftIcon className="h-5 w-5 mr-1" />
                <span className="text-sm">Retour</span>
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-8 relative pt-8">
                Nouveau mot de passe
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-primary -mb-3"></span>
            </h2>
            
            <p className="text-gray-600 mb-6 text-center">
                Créez un nouveau mot de passe sécurisé pour votre compte.
            </p>
            
            <Formik
                initialValues={{ password: '', confirmPassword: '' }}
                validationSchema={validationSchema}
                onSubmit={handleResetPassword}
            >
                {({ isSubmitting }) => (
                    <Form className="w-full flex flex-col items-center space-y-6">
                        <div className="w-full">
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <Field
                                    type="password"
                                    name="password"
                                    className="w-full bg-gray-50 text-gray-700 text-base font-normal pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                                    autoComplete="new-password"
                                    placeholder="••••••••"
                                />
                            </div>
                            <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
                        </div>

                        <div className="w-full">
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <Field
                                    type="password"
                                    name="confirmPassword"
                                    className="w-full bg-gray-50 text-gray-700 text-base font-normal pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                                    autoComplete="new-password"
                                    placeholder="••••••••"
                                />
                            </div>
                            <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-sm mt-1" />
                        </div>

                        <div className="w-full pt-2">
                            <button
                                type="submit"
                                className="w-full flex justify-center py-3 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md shadow-sm transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default ResetPassword;
